/**
 * People seen from ForServices (docs/14-TEAMS-PEOPLE-TEMPLATES.md, "People
 * (ForServices)"; docs/13-ACCESS.md, "People seen from ForServices"): who
 * has a People page and whom it lists, who opens a person's profile, who
 * changes the teams they serve on, and how a person's Planning & people
 * levels read on the Permissions folder tree. The rules only; the planning
 * gate and the screens bring the data.
 */
import {
  atLeast,
  folderChain,
  highest,
  leadsOn,
  levelAnywhere,
  levelRank,
  type Access,
  type FolderTree,
  type GrantScope,
  type LedTeam,
  type PlanningLevel,
} from './access.ts'

// ---- the People page --------------------------------------------------------------------

/**
 * Whom the People page lists them:
 * - `everyone` — Editor or above anywhere (a grant: leading never reaches Editor);
 * - `own_teams` — Viewer or Scheduler anywhere, leading a team included: the
 *   people on their own teams (`isOwnTeam`);
 * - `none` — a Scheduled viewer (or no place in ForServices): no People page.
 */
export type PeopleReach = 'none' | 'own_teams' | 'everyone'

export function peopleReach(access: Access): PeopleReach {
  if (atLeast(levelAnywhere({ ...access, leading: [] }, 'planning'), 'editor')) return 'everyone'
  if (atLeast(levelAnywhere(access, 'planning'), 'viewer')) return 'own_teams'
  return 'none'
}

/**
 * One of their own teams, whose people a Viewer or Scheduler sees on the
 * People page: a team they serve on (a position's roster) or lead, and — for
 * a Scheduler — a team they schedule by a grant: Scheduler granted on one of
 * its service types.
 */
export function isOwnTeam(team: {
  /** They are on one of its positions. */
  onIt: boolean
  leadsIt: boolean
  /** Their granted level on each of its service types. */
  typeLevels: readonly (PlanningLevel | null)[]
}): boolean {
  if (team.onIt || team.leadsIt) return true
  return team.typeLevels.some((level) => atLeast(level, 'scheduler'))
}

// ---- a person's profile -----------------------------------------------------------------

/**
 * Whether they open someone's ForServices profile: the directory reader
 * (`people:read`) and an Editor anywhere (who lists everyone) see anyone;
 * anyone else sees the people serving where they can see — on a team they
 * see, or on a plan they see. Basic details only; contact details have their
 * own rule (`canSeeContactDetails`).
 */
export const canSeeServicesPerson = (input: {
  directory: boolean
  editorSomewhere: boolean
  servesWhereSeen: boolean
}): boolean => input.directory || input.editorSomewhere || input.servesWhereSeen

/**
 * Putting someone on a team's position, or taking them off, from their
 * profile: what is about serving is an Editor's (docs/14). Editor on every
 * service type the team belongs to, as for a team's settings one rung down.
 * Granted levels: leading a team never reaches Editor.
 */
export function canEditRoster(team: { typeLevels: readonly (PlanningLevel | null)[] }): boolean {
  return team.typeLevels.length > 0 && team.typeLevels.every((level) => atLeast(level, 'editor'))
}

// ---- the Permissions folder tree ----------------------------------------------------------

/**
 * Whether they give access anywhere: a church Owner or Admin, or an
 * Administrator of something. Who sees a person's levels at all (each
 * change is still `canGrant`'s).
 */
export const administersSomething = (access: Access): boolean =>
  access.churchAdmin === true || access.grants.some((grant) => grant.level === 'administrator')

/** Where one of a person's Planning & people levels comes from. */
export type LevelSource = 'grant' | 'role' | 'default'

/** One Planning & people level a person holds somewhere, and why. */
export interface PlanningSource {
  level: PlanningLevel
  scope: GrantScope
  from: LevelSource
}

/** What a person holds at one place of the tree, as its row reads. */
export interface PlaceLevel {
  /** Given right here (an access grant on this place): what the row's panel changes. */
  here: PlanningLevel | null
  /**
   * The best that reaches it otherwise: church-wide (a grant, their role,
   * everyone's default) or a folder above. Null when nothing does.
   */
  inherited: PlanningLevel | null
  /** Where `inherited` comes from: the nearest place holding it, or their role or the default. */
  inheritedFrom:
    | { kind: 'church' }
    | { kind: 'folder'; id: string }
    | { kind: 'role' }
    | { kind: 'default' }
    | null
  /** A team they lead serves here (a service type), so they are Scheduler of it. */
  leads: boolean
}

/**
 * A person's Planning & people level at one place of the folder tree: what is
 * set there, and what it inherits from above (13-ACCESS: the highest of the
 * church-wide level and every folder above wins). A church Owner or Admin
 * inherits Administrator everywhere from their role. A service type where a
 * team they lead serves is at least Scheduler for them (docs/14); `leads`
 * says so, and the row says why.
 */
export function placeLevel(
  person: {
    sources: readonly PlanningSource[]
    churchAdmin?: boolean
    leading?: readonly LedTeam[]
  },
  scope: GrantScope,
  tree: FolderTree,
): PlaceLevel {
  const same = (a: GrantScope, b: GrantScope) =>
    a.kind === b.kind && (a.kind === 'org' || (b.kind !== 'org' && a.id === b.id))
  const here = highest(
    person.sources
      .filter((source) => source.from === 'grant' && same(source.scope, scope))
      .map((source) => source.level),
  )

  // Above, nearest first: for a church-wide row, only its role and default.
  const folders =
    scope.kind === 'org'
      ? []
      : scope.kind === 'folder'
        ? folderChain(tree, tree.folders.get(scope.id) ?? null)
        : folderChain(tree, tree.serviceTypes.get(scope.id) ?? null)
  type From = NonNullable<PlaceLevel['inheritedFrom']>
  const candidates: { level: PlanningLevel; from: From }[] = []
  if (person.churchAdmin) candidates.push({ level: 'administrator', from: { kind: 'role' } })
  for (const id of folders)
    for (const source of person.sources)
      if (source.scope.kind === 'folder' && source.scope.id === id)
        candidates.push({ level: source.level, from: { kind: 'folder', id } })
  for (const source of person.sources) {
    if (source.scope.kind !== 'org') continue
    if (source.from === 'grant' && scope.kind === 'org') continue // that is `here`
    candidates.push({
      level: source.level,
      from: source.from === 'grant' ? { kind: 'church' } : { kind: source.from },
    })
  }
  // Highest wins; between equals, the nearest (the order above).
  let best: (typeof candidates)[number] | null = null
  for (const candidate of candidates)
    if (best === null || levelRank(candidate.level) > levelRank(best.level)) best = candidate

  const leads = scope.kind === 'service_type' && leadsOn(person.leading, scope.id)
  return { here, inherited: best?.level ?? null, inheritedFrom: best?.from ?? null, leads }
}
